import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';

export function ForgotPasswordPage() {
  const resetPassword = useAuthStore((s) => s.resetPassword);

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <>
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-brand/10 border border-brand/25 mx-auto mb-4">
          <Mail className="w-6 h-6 text-brand" />
        </div>
        <h2 className="text-xl font-semibold text-gray-100 mb-2 text-center">Check your email</h2>
        <p className="text-sm text-gray-500 text-center mb-6">
          We&apos;ve sent a password reset link to{' '}
          <span className="text-gray-300">{email}</span>. Click the link to set a new password.
        </p>
        <Link
          to="/login"
          className="btn-primary w-full flex items-center justify-center"
        >
          Back to sign in
        </Link>
      </>
    );
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-100 mb-1">Reset password</h2>
      <p className="text-sm text-gray-500 mb-6">
        Enter your email and we&apos;ll send you a reset link.
      </p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="you@example.com"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Mail className="w-4 h-4" />
          {loading ? 'Sending…' : 'Send reset link'}
        </button>
      </form>

      <p className="mt-6 text-sm text-center text-gray-500">
        Remember your password?{' '}
        <Link to="/login" className="text-brand hover:text-brand-light transition-colors">
          Sign in
        </Link>
      </p>
    </>
  );
}
